import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import * as userService from 'services/user';
import * as skillV2Service from 'services/skillV2';
import * as skillService from 'services/skillService';
import type { GlobalRatingPayload } from 'services/skillV2';
import * as skillCategoryService from 'services/skillCategoryService';

import { handleError } from 'utils/errorHandler';
import { getFlattenSkillTree } from 'utils/skillV2';
import type { EndLevelSkillType } from 'utils/reactSelect';

import type { SkillNode } from 'interface/skillTree';
import type { AddSkill, SkillCategory } from 'interface/skill';
import type { ServiceId, ServiceParams } from 'interface/service';
import { GlobalRatingItem, SelectedSkillItem } from 'interface/skill';

import { SKILL_QUERY_KEYS } from './keys';

type UpdateUserSkillsPayload = {
  employeeId: number | string;
  skills: Array<{ id: number | string; rating: number }>;
};

type GlobalRatingResponse = {
  data?: {
    globalRatings?: GlobalRatingItem[];
  };
};

const EMPTY_GLOBAL_RATINGS: GlobalRatingItem[] = [];
const EMPTY_SELECTED_SKILLS: SelectedSkillItem[] = [];

const formatSelectedSkills = (skills: SelectedSkillItem[]): SelectedSkillItem[] => {
  return skills.map((skill) => ({ id: skill.id, rating: skill.rating, skillStatus: skill.skillStatus }));
};

export const GLOBAL_RATINGS_QUERY_KEY = [SKILL_QUERY_KEYS.GLOBAL_RATINGS] as const;

export const ALL_SKILLS_QUERY_KEY = [SKILL_QUERY_KEYS.ALL_SKILLS] as const;

export const getUserSelectedSkillsQueryKey = (employeeId?: number | string) =>
  [SKILL_QUERY_KEYS.SELECTED_SKILLS, employeeId] as const;

export const getSkillsQueryKey = (params: ServiceParams = {}) => [SKILL_QUERY_KEYS.ALL, params] as const;

export const getSkillsV1QueryKey = (params: ServiceParams = {}) => [SKILL_QUERY_KEYS.ALL_V1, params] as const;

export const getSkillCategoriesQueryKey = (params: ServiceParams = {}) =>
  [SKILL_QUERY_KEYS.CATEGORIES, params] as const;

export const getSkillByIdQueryKey = (id?: ServiceId) => [SKILL_QUERY_KEYS.DETAIL, id] as const;

export const END_LEVEL_SKILLS_QUERY_KEY = [SKILL_QUERY_KEYS.END_LEVEL] as const;

export const useGlobalRatingsQuery = (): UseQueryResult<GlobalRatingItem[], Error> => {
  return useQuery<GlobalRatingResponse, Error, GlobalRatingItem[]>({
    queryKey: GLOBAL_RATINGS_QUERY_KEY,
    queryFn: () => skillV2Service.fetchGlobalRating() as Promise<GlobalRatingResponse>,
    select: (response) => response.data?.globalRatings ?? EMPTY_GLOBAL_RATINGS,
    staleTime: Infinity,
  });
};

export const useAllSkillsQuery = (triggerSearchSkillTree?: number): UseQueryResult<EndLevelSkillType[], Error> => {
  return useQuery<{ data: SkillNode[] }, Error, EndLevelSkillType[]>({
    queryKey: [...ALL_SKILLS_QUERY_KEY, triggerSearchSkillTree],
    queryFn: () => skillV2Service.fetchAllSkills() as Promise<{ data: SkillNode[] }>,
    select: (response) => (Array.isArray(response.data) ? getFlattenSkillTree(response.data) : []),
  });
};

export const useUpdateGlobalRatingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, GlobalRatingPayload>({
    mutationFn: async (payload) => {
      await skillV2Service.updateGlobalRating(payload);
    },
    onError: (error) => {
      handleError(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GLOBAL_RATINGS_QUERY_KEY });
    },
  });
};

export const useUserSelectedSkillsQuery = (
  employeeId?: number | string,
  enabled = true
): UseQueryResult<SelectedSkillItem[], Error> => {
  return useQuery<{ data?: SelectedSkillItem[] }, Error, SelectedSkillItem[]>({
    queryKey: getUserSelectedSkillsQueryKey(employeeId),
    queryFn: () => skillService.fetchByUserId(employeeId ?? ''),
    select: (response) =>
      Array.isArray(response.data) ? formatSelectedSkills(response.data as SelectedSkillItem[]) : EMPTY_SELECTED_SKILLS,
    enabled: Boolean(employeeId) && enabled,
  });
};

export const useUpdateUserSelectedSkillsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateUserSkillsPayload>({
    mutationFn: async ({ employeeId, skills }) => {
      await userService.updateSkillsV2(employeeId, { skills });
    },
    onError: (error) => {
      handleError(error);
    },
    onSuccess: (_data, { employeeId, skills }) => {
      queryClient.setQueryData(getUserSelectedSkillsQueryKey(employeeId), skills);
      queryClient.invalidateQueries({ queryKey: getUserSelectedSkillsQueryKey(employeeId) });
    },
  });
};

export const useFetchSkillsQuery = <TData = unknown>(
  params: ServiceParams = {},
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getSkillsQueryKey(params),
    queryFn: () => skillService.fetchAll(params) as Promise<TData>,
    enabled,
  });
};

export const useFetchSkillsV1Query = <TData = unknown>(
  params: ServiceParams = {},
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getSkillsV1QueryKey(params),
    queryFn: () => skillService.fetchAllV1(params) as Promise<TData>,
    enabled,
  });
};

export const useFetchSkillByIdQuery = <TData = unknown>(
  id?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getSkillByIdQueryKey(id),
    queryFn: () => skillService.fetchById(id!) as Promise<TData>,
    enabled: Boolean(id) && enabled,
  });
};

export const useFetchSkillCategoriesQuery = <TData = unknown>(
  params: ServiceParams = {},
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getSkillCategoriesQueryKey(params),
    queryFn: () => skillCategoryService.fetchAll(params) as Promise<TData>,
    enabled,
  });
};

export const useFetchEndLevelSkillsQuery = <TData = unknown>(enabled = true): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: END_LEVEL_SKILLS_QUERY_KEY,
    queryFn: () => skillV2Service.fetchEndLevelSkills() as Promise<TData>,
    enabled,
  });
};

const useInvalidateSkillsV1 = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [SKILL_QUERY_KEYS.ALL_V1] });
    queryClient.invalidateQueries({ queryKey: [SKILL_QUERY_KEYS.CATEGORIES] });
  };
};

const useInvalidateSkillsV2 = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [SKILL_QUERY_KEYS.ALL] });
    queryClient.invalidateQueries({ queryKey: END_LEVEL_SKILLS_QUERY_KEY });
  };
};

export const useCreateSkillMutation = () => {
  const invalidateSkills = useInvalidateSkillsV1();

  return useMutation({
    mutationFn: (skill: AddSkill) => skillService.create(skill),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useUpdateSkillMutation = () => {
  const invalidateSkills = useInvalidateSkillsV1();

  return useMutation({
    mutationFn: ({ skill, id }: { skill: AddSkill; id: ServiceId }) => skillService.update(skill, id),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useDeleteSkillMutation = () => {
  const invalidateSkills = useInvalidateSkillsV1();

  return useMutation({
    mutationFn: (id: ServiceId) => skillService.remove(id),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useCreateSkillCategoryMutation = () => {
  const invalidateSkills = useInvalidateSkillsV1();

  return useMutation({
    mutationFn: (category: SkillCategory) => skillCategoryService.create(category),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useUpdateSkillCategoryMutation = () => {
  const invalidateSkills = useInvalidateSkillsV1();

  return useMutation({
    mutationFn: ({ category, id }: { category: SkillCategory; id: ServiceId }) =>
      skillCategoryService.update(category, id),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useDeleteSkillCategoryMutation = () => {
  const invalidateSkills = useInvalidateSkillsV1();

  return useMutation({
    mutationFn: (id: ServiceId) => skillCategoryService.remove(id),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useCreateSkillV2Mutation = () => {
  const invalidateSkills = useInvalidateSkillsV2();

  return useMutation({
    mutationFn: (body: Parameters<typeof skillV2Service.create>[0]) => skillV2Service.create(body),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useUpdateSkillV2Mutation = () => {
  const invalidateSkills = useInvalidateSkillsV2();

  return useMutation({
    mutationFn: ({ skillId, body }: { skillId: ServiceId; body: Parameters<typeof skillV2Service.update>[1] }) =>
      skillV2Service.update(skillId, body),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useDeleteSkillV2Mutation = () => {
  const invalidateSkills = useInvalidateSkillsV2();

  return useMutation({
    mutationFn: (id: ServiceId) => skillV2Service.deleteSkill(id),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useCreateSkillV2CategoryMutation = () => {
  const invalidateSkills = useInvalidateSkillsV2();

  return useMutation({
    mutationFn: (body: Parameters<typeof skillV2Service.createCategory>[0]) => skillV2Service.createCategory(body),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useUpdateSkillV2CategoryMutation = () => {
  const invalidateSkills = useInvalidateSkillsV2();

  return useMutation({
    mutationFn: ({
      categoryId,
      body,
    }: {
      categoryId: ServiceId;
      body: Parameters<typeof skillV2Service.updateCategory>[1];
    }) => skillV2Service.updateCategory(categoryId, body),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};

export const useDeleteSkillV2CategoryMutation = () => {
  const invalidateSkills = useInvalidateSkillsV2();

  return useMutation({
    mutationFn: (id: ServiceId) => skillV2Service.deleteCategory(id),
    onSuccess: invalidateSkills,
    onError: (error) => handleError(error),
  });
};
